import { api } from './client';
import type {
  KnowledgePageDto,
  KnowledgePageVersionDto,
  KnowledgeSharingDto,
  KnowledgeTagDto,
  ThoughtDto
} from './types';

// --- pages ---

export function listKnowledgeTree(category?: string): Promise<KnowledgePageDto[]> {
  return api<KnowledgePageDto[]>('/knowledge/pages', { query: { category } });
}

export function getKnowledgePage(id: string): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>(`/knowledge/pages/${id}`);
}

export function createKnowledgePage(body: {
  paraCategory: string;
  parentId?: string | null;
  title: string;
  icon?: string | null;
  bodyMarkdown?: string;
}): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>('/knowledge/pages', { method: 'POST', body });
}

export function updateKnowledgePage(
  id: string,
  patch: Record<string, unknown>
): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>(`/knowledge/pages/${id}`, { method: 'PATCH', body: patch });
}

export function deleteKnowledgePage(id: string): Promise<void> {
  return api<void>(`/knowledge/pages/${id}`, { method: 'DELETE' });
}

/** Re-parents a page; a null parentId makes it top-level in paraCategory. */
export function moveKnowledgePage(
  id: string,
  target: { parentId?: string | null; paraCategory?: string }
): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>(`/knowledge/pages/${id}/move`, { method: 'POST', body: target });
}

export function archiveKnowledgePage(id: string): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>(`/knowledge/pages/${id}/archive`, { method: 'POST' });
}

export function setKnowledgePageTags(id: string, tagIds: string[]): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>(`/knowledge/pages/${id}/tags`, { method: 'PUT', body: { tagIds } });
}

// --- search (semantic + keyword fallback, same endpoint the MCP uses) ---

export interface SearchHit {
  kind: string;
  id: string;
  pageId: string | null;
  title: string | null;
  heading: string | null;
  snippet: string | null;
  score: number;
  match: string;
  /** Kategoria PARA i korzeń gałęzi - żeby trafienie mówiło, skąd pochodzi. */
  paraCategory: string | null;
  branch: string | null;
}

export function searchKnowledge(q: string, limit = 15, includeArchive = false): Promise<SearchHit[]> {
  return api<SearchHit[]>('/search', {
    query: { q, scope: 'knowledge', limit, includeArchive: includeArchive ? 'true' : undefined }
  });
}

// --- sharing ---

export function getKnowledgeSharing(id: string): Promise<KnowledgeSharingDto> {
  return api<KnowledgeSharingDto>(`/knowledge/pages/${id}/sharing`);
}

export function setKnowledgeSharing(
  id: string,
  body: {
    visibility: string;
    shareScope?: string;
    userIds?: string[];
    emails?: string[];
    notifyByEmail?: boolean;
  }
): Promise<KnowledgeSharingDto> {
  return api<KnowledgeSharingDto>(`/knowledge/pages/${id}/sharing`, { method: 'PUT', body });
}

export function listSharedWithMe(): Promise<KnowledgePageDto[]> {
  return api<KnowledgePageDto[]>('/knowledge/pages/shared');
}

/** Unauthenticated public read (works without a session). */
export function getPublicPage(id: string): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>(`/public/knowledge/${id}`);
}

export function getPublicChildren(id: string): Promise<KnowledgePageDto[]> {
  return api<KnowledgePageDto[]>(`/public/knowledge/${id}/children`);
}

// --- versions ---

export function listPageVersions(id: string): Promise<KnowledgePageVersionDto[]> {
  return api<KnowledgePageVersionDto[]>(`/knowledge/pages/${id}/versions`);
}

export function revertPage(id: string, versionNo: number): Promise<KnowledgePageDto> {
  return api<KnowledgePageDto>(`/knowledge/pages/${id}/revert/${versionNo}`, { method: 'POST' });
}

// --- thoughts ---

export function listThoughts(page = 0, size = 50): Promise<ThoughtDto[]> {
  return api<ThoughtDto[]>('/knowledge/thoughts', { query: { page, size } });
}

export function searchThoughts(q: string): Promise<ThoughtDto[]> {
  return api<ThoughtDto[]>('/knowledge/thoughts/search', { query: { q } });
}

export function createThought(body: string, source?: string): Promise<ThoughtDto> {
  return api<ThoughtDto>('/knowledge/thoughts', { method: 'POST', body: { body, source } });
}

export function deleteThought(id: string): Promise<void> {
  return api<void>(`/knowledge/thoughts/${id}`, { method: 'DELETE' });
}

// --- tags ---

export function listKnowledgeTags(): Promise<KnowledgeTagDto[]> {
  return api<KnowledgeTagDto[]>('/knowledge/tags');
}

export function createKnowledgeTag(name: string, color?: string): Promise<KnowledgeTagDto> {
  return api<KnowledgeTagDto>('/knowledge/tags', { method: 'POST', body: { name, color } });
}

export function deleteKnowledgeTag(id: string): Promise<void> {
  return api<void>(`/knowledge/tags/${id}`, { method: 'DELETE' });
}
